import { useEffect, useRef } from "react";
import { useAppSelector } from "@/stores/hook";
import TypingDots from "./TypingDots";
import CuteErrorBubble from "./CuteErrorBubble";
import MarkdownText from "./MarkdownText";
import ToolCallsSummary from "./ToolCallsSummary";
import RecordingCards from "./RecordingCards";
import ChartCards from "./ChartCards";
import AutomationDraftCards from "./AutomationDraftCards";
import { useAIChat } from "../hooks/useAIChat";

// ---------------------------------------------------------------------------
// Renders the running thread. Auto-scrolls to bottom on every new token.
// Plain inline-styled bubbles to stay independent of the Tailwind / shadcn
// theme so this can drop in regardless of the rest of the design system.
// ---------------------------------------------------------------------------

// Empty-state starter prompts. ANALYTICS asks the assistant for numbers;
// LEARN asks it to teach how the CRM works (newbie onboarding). Clicking one
// just sends it through the normal send() pipeline. BUILD asks it to draft an
// automation, which builds itself live in the automation builder (unsaved).
const STARTERS = {
  build: [
    "Create an automation: assign every new lead to counsellors equally",
    "Build a rule: add a follow-up for tomorrow when a lead is assigned",
  ],
  analytics: [
    "How many leads today?",
    "Compare this week vs last",
    "Who's my best counsellor this month?",
  ],
  learn: [
    "What does 'overdue' mean?",
    "What's the difference between a lead and a deal?",
    "Which stage should I move an interested lead to?",
    "Explain my pipeline stages",
  ],
};

export default function AIMessageList() {
  const messages = useAppSelector((s) => s.aiChat.messages);
  const lastError = useAppSelector((s) => s.aiChat.lastError);
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const { send } = useAIChat();

  useEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, lastError]);

  if (!messages.length) {
    return (
      <div
        ref={scrollerRef}
        style={{
          flex: 1,
          padding: 20,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          color: "var(--color-text-secondary, #666)",
        }}
      >
        <div style={{ fontSize: 36, marginBottom: 8 }}>✨</div>
        <div style={{ fontWeight: 600, fontSize: 14 }}>Ask Xale AI</div>
        <div style={{ fontSize: 12, marginTop: 6, maxWidth: 300, lineHeight: 1.5 }}>
          Ask about your data, build an automation, or learn how the CRM works. Tap to try one:
        </div>

        <StarterGroup title="Analytics" prompts={STARTERS.analytics} onPick={send} />
        <StarterGroup title="Build automations" prompts={STARTERS.build} onPick={send} />
        <StarterGroup title="Learn the CRM" prompts={STARTERS.learn} onPick={send} />
      </div>
    );
  }

  return (
    <div
      ref={scrollerRef}
      style={{
        flex: 1,
        padding: "12px 14px",
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      {messages.map((m) => (
        <div
          key={m.id}
          style={{
            alignSelf: m.role === "user" ? "flex-end" : "flex-start",
            maxWidth: "92%",
          }}
        >
          {m.role === "user" ? (
            <div
              style={{
                background: "var(--color-success, #156548)",
                color: "#fff",
                padding: "8px 12px",
                borderRadius: "12px 12px 4px 12px",
                fontSize: 13,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {m.content}
            </div>
          ) : (
            <div>
              {m.toolCalls && m.toolCalls.length > 0 && (
                <ToolCallsSummary
                  toolCalls={m.toolCalls}
                  compact={m.isStreaming}
                />
              )}
              {m.toolCalls && m.toolCalls.length > 0 && (
                <RecordingCards toolCalls={m.toolCalls} />
              )}
              {m.toolCalls && m.toolCalls.length > 0 && (
                <ChartCards toolCalls={m.toolCalls} />
              )}
              {m.toolCalls && m.toolCalls.length > 0 && (
                <AutomationDraftCards toolCalls={m.toolCalls} />
              )}
              {(() => {
                // Three states for the assistant bubble:
                //   1. Streaming but no text yet (e.g. waiting on tool result)
                //      → render TypingDots only (no empty grey square).
                //   2. Streaming with partial text → text + soft caret.
                //   3. Done with text → text.
                const hasText = !!m.content;
                if (m.isStreaming && !hasText) {
                  // Pick a label that hints at what's happening, if a tool
                  // chip just fired we want "Working" not "Thinking".
                  const lastTool = m.toolCalls?.[m.toolCalls.length - 1];
                  const label = lastTool
                    ? lastTool.result !== undefined
                      ? "Composing answer"
                      : `Running ${lastTool.name}`
                    : "Thinking";
                  return (
                    <div
                      style={{
                        background: "var(--color-black-2, #f5f5f5)",
                        padding: "10px 12px",
                        borderRadius: "12px 12px 12px 4px",
                        display: "inline-flex",
                      }}
                    >
                      <TypingDots label={label} />
                    </div>
                  );
                }
                return (
                  <div
                    style={{
                      background: "var(--color-black-2, #f5f5f5)",
                      color: "var(--color-text-primary, #1a1a1a)",
                      padding: "8px 12px",
                      borderRadius: "12px 12px 12px 4px",
                      fontSize: 13,
                      wordBreak: "break-word",
                      lineHeight: 1.5,
                    }}
                  >
                    <MarkdownText content={m.content} />
                    {m.isStreaming && (
                      <span style={{ opacity: 0.5, marginLeft: 2 }}>▍</span>
                    )}
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      ))}
      {lastError && <CuteErrorBubble message={lastError} />}
    </div>
  );
}

// A labelled column of clickable starter-prompt chips for the empty state.
function StarterGroup({
  title,
  prompts,
  onPick,
}: {
  title: string;
  prompts: string[];
  onPick: (text: string) => void;
}) {
  return (
    <div style={{ marginTop: 16, width: "100%", maxWidth: 300 }}>
      <div
        style={{
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: 0.4,
          textTransform: "uppercase",
          color: "var(--color-text-gray, #999)",
          marginBottom: 6,
        }}
      >
        {title}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, justifyContent: "center" }}>
        {prompts.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => onPick(p)}
            style={{
              background: "var(--color-black-2, #f5f5f5)",
              color: "var(--color-text-primary, #1a1a1a)",
              border: "1px solid var(--color-black-3, #ececec)",
              borderRadius: 999,
              padding: "6px 10px",
              fontSize: 12,
              lineHeight: 1.3,
              cursor: "pointer",
            }}
          >
            {p}
          </button>
        ))}
      </div>
    </div>
  );
}
